import { Injectable } from '@angular/core';
import { NbAuthService } from '@nebular/auth';
import * as Sentry from '@sentry/browser';
import * as R from 'ramda';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  OperatorFunction,
  ReplaySubject,
  Subject,
} from 'rxjs';
import { catchError, filter, map, scan, startWith, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../api';
import { IdNameMixin } from '../api/models';

interface IdObject {
  id: number;
}
export interface IdMap<T extends IdObject> {
  [index: number]: T;
}

interface LoadingState<T = unknown> {
  loading: boolean;
  error?: Error | null;
  data?: T;
}

export function toLoadingState<T>(): OperatorFunction<any, LoadingState<T>> {
  return (source: Observable<any>) =>
    source.pipe(
      map((data: T) => ({ data, loading: false })),
      catchError((error) => of({ error, loading: false })),
      startWith({ error: null, loading: true }),
      scan((state: LoadingState<T>, change: LoadingState<T>) => ({
        ...state,
        ...change,
      }))
    );
}

export const idMapToArray = <T extends IdObject>(): OperatorFunction<
  IdMap<T>,
  T[]
> =>
  map((idMap) => {
    const list: T[] = Object.values(idMap);

    return R.sortWith(
      [
        // @ts-ignore
        R.ascend(R.prop('ordering')),
        // @ts-ignore
        R.ascend(R.prop('order')),
      ],
      list,
    );
  });

export const arrayToIdMap = <T extends IdObject>(): OperatorFunction<
  T[],
  IdMap<T>
> =>
  map((items) => {
    return items.reduce(
      (itemsMap, item) => ({
        ...itemsMap,
        [item.id]: item,
      }),
      {},
    );
  });

@Injectable()
export class ScopeService {
  /** Whether the perimeters are currently loading. */
  public perimetersLoading$ = new BehaviorSubject<boolean>(true);

  private _config$: ReplaySubject<KPI.Config>;
  private _user$: ReplaySubject<KPI.User>;
  private subjects: { [name: string]: ReplaySubject<IdMap<any>> } = {};
  private refreshSubjects: { [name: string]: Subject<void> } = {};
  public transformImportedData = (item: any) => {
    for (const key in item) {
      if (item.hasOwnProperty(key) && key.includes('_')) {
        const strings: string[] = key.split('_');
        if (Number.isInteger(+strings[strings.length - 1])) {
          const year = strings.pop();
          item[year] = { ...(item[year] || {}) };
          item[year][strings.join('_')] = item[key];
          delete item[key];
        }
      }
    }
    return item;
  }
  //#endregion POC Energy

  constructor(private authService: NbAuthService, private api: APIService) { }

  /**
   * The parent perimeter may have a reference value that is not repeated on the children,
   * e.g. the city. To circumvent this, let's add the reference value appearing on the parent
   * that are not on the children. This way, we can match a child even if the searched value is
   * only on the parent.
   */
  public static mapPerimetersFilterRefData(
    perimeters: KPI.Perimeter[],
  ): KPI.Perimeter[] {
    return perimeters.map((perimeter) => ({
      ...perimeter,
      _filter_ref_data:
        perimeter._filter_ref_data || [].concat(perimeter.ref_data),
      sub_perimeters: perimeter.sub_perimeters
        ? perimeter.sub_perimeters.map((subPerimeter) => ({
          ...subPerimeter,
          _filter_ref_data: [].concat(
            subPerimeter.ref_data,
            perimeter.ref_data
              ? perimeter.ref_data.filter(
                (refData) =>
                  !refData.aggregated &&
                  subPerimeter.ref_data?.findIndex(
                    (subPerimeterRefData) =>
                      subPerimeterRefData.year === refData.year &&
                      subPerimeterRefData.var_name === refData.var_name,
                  ) === -1,
              )
              : [],
          ),
        }))
        : [],
    }));
  }

  /** Global backend configuration. */
  public get config$(): ReplaySubject<KPI.Config> {
    if (!this._config$) {
      const subject = new ReplaySubject<KPI.Config>(1);
      const refreshSubject = new Subject<void>();

      merge(
        this.authService
          .onAuthenticationChange()
          .pipe(filter((authenticated) => authenticated === true)),
        refreshSubject,
      )
        .pipe(switchMap(() => this.api.config_config()))
        .subscribe((config) =>
          subject.next({
            ...config,
            margin_consistency_technical_state_lifetime: Number(
              config.margin_consistency_technical_state_lifetime,
            ),
          }),
        );

      this._config$ = subject;
      this.refreshSubjects['config$'] = refreshSubject;
    }

    return this._config$;
  }

  public refreshConfig(): void {
    this.refresh('config$');
  }

  /** Currently logged-in user subject. */
  public get user$(): ReplaySubject<KPI.User> {
    if (!this._user$) {
      const subject = new ReplaySubject<KPI.User>(1);
      const refreshSubject = new Subject<void>();

      merge(
        this.authService
          .onAuthenticationChange()
          .pipe(filter((authenticated) => authenticated === true)),
        refreshSubject,
      )
        .pipe(
          switchMap(() => this.api.users_members_me()),
          tap((user) => {
            Sentry.configureScope((scope) => {
              scope.setUser({
                id: user.id.toString(),
                email: user.email,
              });
            });
          }),
        )
        .subscribe((user) => subject.next(user));

      this._user$ = subject;
      this.refreshSubjects['user$'] = refreshSubject;
    }

    return this._user$;
  }

  public refreshUser(): void {
    this.refresh('user$');
  }

  /** List of clusters */
  public get clusters$(): ReplaySubject<IdMap<KPI.Cluster>> {
    return this.getReplaySubject('clusters$', () =>
      this.api.structures_clusters(),
    );
  }

  public getCustomerClusters(perimeters): Observable<KPI.Cluster[]> {
    return combineLatest(this.clusters$.pipe(idMapToArray())).pipe(
      map(([clusters]) => {
        return clusters.filter((c) =>
          perimeters.some((p) => p.cluster === c.id),
        );
      }),
    );
  }

  public refreshClusters(): void {
    this.refresh('clusters$');
  }

  /** List of perimeter types */
  public get perimeterTypes$(): ReplaySubject<IdMap<KPI.PerimeterType>> {
    return this.getReplaySubject('perimeterTypes$', () =>
      this.api.structures_perimeter_types(),
    );
  }

  public getCustomerPerimeterTypes(
    perimeters: KPI.Perimeter[]
  ): Observable<KPI.PerimeterType[]> {
    return this.perimeterTypes$.pipe(
      idMapToArray(),
      map((perimeterTypes: KPI.PerimeterType[]) => {
        const subPerimeters = perimeters.flatMap(p => p.sub_perimeters);
        const usedPerimeterTypeIds = new Set(
          subPerimeters.map(sp => sp.perimeter_type)
        );
        return perimeterTypes.filter(pt =>
          usedPerimeterTypeIds.has(pt.id)
        );
      })
    );
  }

  public refreshPerimeterTypes(): void {
    this.refresh('perimeterTypes$');
  }

  private _perimeters$: ReplaySubject<IdMap<KPI.Perimeter>>;
  private _perimetersRefresh$ = new Subject<void>();
  
  /** Perimeters the current user has access to. */
  public get perimeters$(): ReplaySubject<IdMap<KPI.Perimeter>> {
    if (!this._perimeters$) {
      this._perimeters$ = new ReplaySubject(1);
      
      merge(
        this.authService.onAuthenticationChange().pipe(
          filter((authenticated) => !!authenticated)
        ),
        this._perimetersRefresh$
      ).pipe(
        switchMap(() => this.api.audit_perimeters()),
        tap(() => this.perimetersLoading$.next(true)),
        map((perimeters) =>
          ScopeService.mapPerimetersFilterRefData(perimeters),
        ),
        tap(() => this.perimetersLoading$.next(false)),
      )
      .subscribe((perimeters) => {
        this._perimeters$.next(perimeters);
      });
    }

    return this._perimeters$;
  }

  public refreshPerimeters(): void {
    this._perimetersRefresh$.next();
  }

  private _monoperimeters$: ReplaySubject<IdMap<KPI.Perimeter>>;
  /** Mono-perimeters the current user has access to. */
  public get monoperimeters$(): ReplaySubject<IdMap<KPI.Perimeter>> {
    if (!this._monoperimeters$) {
      this._monoperimeters$ = new ReplaySubject(1);
      const monoPerimeters = [];
      this.perimeters$.pipe(idMapToArray()).subscribe((perimeters) => {
        perimeters.map((perimeter) => {
          perimeter.sub_perimeters.map((subperim) => {
            subperim.name = perimeter.name + ' - ' + subperim.name;
            monoPerimeters.push(subperim);
          });
        });
        this._monoperimeters$.next(monoPerimeters);
      });
    }

    return this._monoperimeters$;
  }

  public get assetTypes$(): ReplaySubject<IdMap<KPI.AssetType>> {
    const streamFactory = () =>
      this.api.assets_asset_types(null, true).pipe(
        map((assetTypes) =>
          assetTypes.map((assetType) => ({
            ...assetType,
            // Cast budget reference prices
            ...(assetType.budget_reference_data
              ? {
                budget_reference_data: {
                  ...assetType.budget_reference_data,
                  budget_price: Number(
                    assetType.budget_reference_data.budget_price,
                  ),
                  material_price: Number(
                    assetType.budget_reference_data.material_price,
                  ),
                  additional_price: Number(
                    assetType.budget_reference_data.additional_price,
                  ),
                  workloads: assetType.budget_reference_data.workloads.map(
                    (workload) => ({
                      ...workload,
                      hourly_rate: Number(workload.hourly_rate),
                      hours_number: Number(workload.hours_number),
                    }),
                  ),
                },
              }
              : {}),
          })),
        ),
      );
    return this.getReplaySubject('assetTypes$', streamFactory);
  }

  public refreshAssetTypes(): void {
    this.refresh('assetTypes$');
  }

  public get zones$(): ReplaySubject<IdMap<KPI.Zone>> {
    return this.getReplaySubject('zones$', () =>
      this.api.assets_zones(null, true),
    );
  }

  public refreshZones(): void {
    this.refresh('zones$');
  }

  public get superCategories$(): ReplaySubject<IdMap<KPI.SuperCategory>> {
    return this.getReplaySubject('superCategories$', () =>
      this.api.assets_super_categories(null, true),
    );
  }

  public refreshSuperCategories(): void {
    this.refresh('superCategories$');
  }

  public get refrigerantTypes$(): ReplaySubject<IdMap<KPI.RefrigerantType>> {
    return this.getReplaySubject('refrigerantTypes$', () =>
      this.api.assets_refrigerant_types(),
    );
  }

  public get categories$(): ReplaySubject<IdMap<KPI.Category>> {
    return this.getReplaySubject('categories$', () =>
      this.api.assets_categories(null, true),
    );
  }

  public refreshCategories(): void {
    this.refresh('categories$');
  }

  public get subCategories$(): ReplaySubject<IdMap<KPI.SubCategory>> {
    return this.getReplaySubject('subCategories$', () =>
      this.api.assets_sub_categories(null, true),
    );
  }

  public refreshSubCategories(): void {
    this.refresh('subCategories$');
  }

  public get ownershipTypes$(): ReplaySubject<IdMap<KPI.OwnershipType>> {
    return this.getReplaySubject('ownershipTypes$', () =>
      this.api.assets_ownership_types(null, true),
    );
  }

  public refreshOwnershipTypes(): void {
    this.refresh('ownershipTypes$');
  }

  public get investmentBudgetOrigins$(): ReplaySubject<
    IdMap<KPI.InvestmentBudgetOrigin>
  > {
    return this.getReplaySubject('investmentBudgetOrigins$', () =>
      this.api.investments_investment_budget_origin(),
    );
  }

  public refreshInvestmentBudgetOrigins(): void {
    this.refresh('investmentBudgetOrigins$');
  }

  public get investmentInvoicesRecipient$(): ReplaySubject<
    IdMap<KPI.InvestmentInvoiceRecipient>
  > {
    return this.getReplaySubject('investmentInvoicesRecipient$', () =>
      this.api.investments_invoices_recipient(),
    );
  }

  public refreshinvestmentInvoicesRecipient(): void {
    this.refresh('investmentInvoicesRecipient$');
  }

  public get investmentSponsors$(): ReplaySubject<IdMap<KPI.Sponsor>> {
    return this.getReplaySubject('investmentSponsors$', () =>
      this.api.investments_sponsor(),
    );
  }

  public refresinvestmentSponsors(): void {
    this.refresh('investmentSponsors$');
  }

  public get investmentStatuses$(): ReplaySubject<IdMap<KPI.InvestmentStatus>> {
    return this.getReplaySubject('investmentStatuses$', () =>
      this.api.investments_investment_statuses(),
    );
  }

  public refreshInvestmentStatuses(): void {
    this.refresh('investmentStatuses$');
  }

  public get investmentTypes$(): ReplaySubject<IdMap<KPI.InvestmentType>> {
    return this.getReplaySubject('investmentTypes$', () =>
      this.api.investments_investment_types(),
    );
  }

  public refreshInvestmentTypes(): void {
    this.refresh('investmentTypes$');
  }

  public get auditQuestions$(): ReplaySubject<IdMap<KPI.AuditQuestion>> {
    return this.getReplaySubject('auditQuestions$', () =>
      this.api.audit_questions(null, true),
    );
  }

  public refreshAuditQuestions(): void {
    this.refresh('auditQuestions$');
  }

  public get referenceFields$(): ReplaySubject<IdMap<KPI.ReferenceField>> {
    return this.getReplaySubject('referenceFields$', () =>
      this.api.reference_data_reference_fields(null, true),
    );
  }

  public refreshReferenceFields(): void {
    this.refresh('referenceFields$');
  }

  public get investmentPriorities$(): ReplaySubject<
    IdMap<KPI.InvestmentPriority>
  > {
    return this.getReplaySubject('investmentPriorities$', () =>
      this.api.investments_investment_priorities().pipe(
        map(priorities => [...priorities].sort((a, b) => a.ordering - b.ordering)),
      ),
    );
  }

  public refreshInvestmentPriorities(): void {
    this.refresh('investmentPriorities$');
  }

  public get ratingReasons$(): ReplaySubject<IdMap<KPI.RatingReason>> {
    return this.getReplaySubject('ratingReasons$', () =>
      this.api.audit_rating_reasons(),
    );
  }

  public refreshRatingReasons(): void {
    this.refresh('ratingReasons$');
  }

  public get assetPowerSources$(): ReplaySubject<IdMap<KPI.AssetPowerSource>> {
    return this.getReplaySubject('assetPowerSources$', () =>
      this.api.assets_power_sources(),
    );
  }

  public refreshAssetPowersources(): void {
    this.refresh('assetPowerSources$');
  }

  public get assetFloors$(): ReplaySubject<IdMap<KPI.AssetFloor>> {
    return this.getReplaySubject('assetFloors$', () =>
      this.api.assets_floors(null, true),
    );
  }

  public refreshAssetFloors(): void {
    this.refresh('assetFloors$');
  }

  //#endregion POC Energy

  private getReplaySubject<T extends IdObject>(
    name: string,
    streamFactory: () => Observable<T[]>,
  ): ReplaySubject<IdMap<T>> {
    if (!this.subjects[name]) {
      const subject = new ReplaySubject<IdMap<T>>(1);
      const refreshSubject = new Subject<void>();

      merge(
        this.authService
          .onAuthenticationChange()
          .pipe(filter((authenticated) => authenticated === true)),
        refreshSubject,
      )
        .pipe(
          switchMap(() => streamFactory()),
          arrayToIdMap(),
        )
        .subscribe((data) => subject.next(data));

      this.subjects[name] = subject;
      this.refreshSubjects[name] = refreshSubject;
    }

    return this.subjects[name];
  }

  private refresh(name: string): void {
    if (this.refreshSubjects[name]) {
      this.refreshSubjects[name].next();
    }
  }

  public get getInvestFurtherInfo$(): ReplaySubject<IdMap<IdNameMixin>> {
    return this.getReplaySubject('getInvestFurtherInfo$', () =>
      this.api.investments_investment_further_informations(null, true),
    );
  }

  public get taxonomyCategories$(): ReplaySubject<IdMap<KPI.TaxonomyCategory>> {
    return this.getReplaySubject('taxonomyCategories$', () =>
      this.api.get_taxonomy_categories(),
    );
  }

  public get taxonomyObjectives$(): ReplaySubject<IdMap<KPI.TaxonomyObjective>> {
    return this.getReplaySubject('taxonomyObjectives$', () =>
      this.api.get_taxonomy_objectives(),
    );
  }

  public get heatTransferCoefficientU$(): ReplaySubject<IdMap<IdNameMixin>> {
    return this.getReplaySubject('heatTransferCoefficientU$', () =>
      this.api.get_heat_transfer_coefficient_u(),
    );
  }

  public get sourceInfoHeatTransferCoefficientU$(): ReplaySubject<IdMap<IdNameMixin>> {
    return this.getReplaySubject('sourceInfoHeatTransferCoefficientU$', () =>
      this.api.get_source_info_heat_transfer_coefficient_u(),
    );
  }

  public get thermalResistanceCoefficientR$(): ReplaySubject<IdMap<IdNameMixin>> {
    return this.getReplaySubject('thermalResistanceCoefficientR$', () =>
      this.api.get_thermal_resistance_coefficient_r(),
    );
  }

  public get sourceInfoThermalResistanceCoefficientR$(): ReplaySubject<IdMap<IdNameMixin>> {
    return this.getReplaySubject('sourceInfoThermalResistanceCoefficientR$', () =>
      this.api.get_source_info_thermal_resistance_coefficient_r(),
    );
  }

  public get budgetScopes$(): ReplaySubject<IdMap<KPI.BudgetScope>> {
    return this.getReplaySubject('budgetScopes$', () =>
      this.api.get_budget_scopes(),
    );
  }

  public get confidencesOnFeasibility$(): ReplaySubject<IdMap<IdNameMixin>> {
    return this.getReplaySubject('confidencesOnFeasibility$', () =>
      this.api.get_confidences_on_feasibility(),
    );
  }

  public get countries$(): ReplaySubject<IdMap<KPI.Country>> {
    return this.getReplaySubject('countries$', () =>
      this.api.structures_countries(),
    );
  }

}
